import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPasswordPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== password2) {
      toast.error(t("Şifreler eşleşmiyor"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 auth-noise" data-testid="reset-password-page">
      <header className="p-8">
        <Link to="/login"><Wordmark /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-10 fade-up">
          {!token && (
            <div data-testid="reset-no-token">
              <XCircle className="h-12 w-12 text-red-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Geçersiz bağlantı")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("Şifre sıfırlama bağlantısı geçersiz. Yeni bir bağlantı isteyin.")}
              </p>
              <Button asChild className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/forgot-password" data-testid="reset-goto-forgot-button">{t("Yeni bağlantı isteyin")}</Link>
              </Button>
            </div>
          )}
          {token && done && (
            <div data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Şifreniz güncellendi")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("Yeni şifrenizle giriş yapabilirsiniz.")}
              </p>
              <Button asChild className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/login" data-testid="reset-goto-login-button">{t("Giriş Yap")}</Link>
              </Button>
            </div>
          )}
          {token && !done && (
            <div data-testid="reset-form-wrap">
              <KeyRound className="h-12 w-12 text-blue-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Yeni şifrenizi belirleyin")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("En az 8 karakterli yeni bir şifre girin.")}
              </p>
              <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="reset-password">{t("Yeni Şifre")}</Label>
                  <Input id="reset-password" type="password" required minLength={8} value={password}
                    onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                    placeholder="••••••••" data-testid="reset-password-input"
                    className="focus-visible:ring-blue-600" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password2">{t("Yeni Şifre (Tekrar)")}</Label>
                  <Input id="reset-password2" type="password" required minLength={8} value={password2}
                    onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password"
                    placeholder="••••••••" data-testid="reset-password2-input"
                    className="focus-visible:ring-blue-600" />
                </div>
                <Button type="submit" disabled={saving} data-testid="reset-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Şifremi Sıfırla")}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
